import React, { useState } from 'react';
import type { Deployment, WorkflowStatus } from '../types/api';

interface DeploymentMonitorProps {
  deployments: Deployment[];
  workflows?: WorkflowStatus[];
  loading?: boolean;
  onCancel?: (deploymentId: string) => void;
  onRetry?: (deploymentId: string) => void;
}

export function DeploymentMonitor({
  deployments,
  workflows = [],
  loading,
  onCancel,
  onRetry,
}: DeploymentMonitorProps) {
  const [selectedDeployment, setSelectedDeployment] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<Deployment['status'] | ''>('');

  // Filter deployments
  const filteredDeployments = deployments.filter((deployment) => {
    if (filterStatus === '') return true;
    return deployment.status === filterStatus;
  });

  // Get workflow for deployment
  const getWorkflow = (deploymentId: string) => {
    return workflows.find((w) => w.params.deploymentId === deploymentId);
  };

  const getStatusColor = (status: Deployment['status']) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50';
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'rolled_back':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: Deployment['status']) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'in_progress':
        return '🔄';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'rolled_back':
        return '⏪';
      default:
        return '❓';
    }
  };

  const getProgressPercentage = (deployment: Deployment) => {
    if (!deployment.stats) return 0;
    const { success, failed, pending } = deployment.stats;
    const total = success + failed + pending;
    if (total === 0) return 0;
    return Math.round(((success + failed) / total) * 100);
  };

  const formatDate = (date: string | number) => {
    return new Date(date).toLocaleString();
  };

  const formatDuration = (start: string | number, end?: string | number) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const duration = endTime - startTime;

    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filter by status:</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as Deployment['status'] | '')}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="rolled_back">Rolled Back</option>
          </select>

          <div className="ml-auto text-sm text-gray-600">
            Showing {filteredDeployments.length} of {deployments.length} deployments
          </div>
        </div>
      </div>

      {/* Deployments List */}
      <div className="space-y-4">
        {filteredDeployments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No deployments found.
          </div>
        ) : (
          filteredDeployments.map((deployment) => {
            const workflow = getWorkflow(deployment.id);
            const isExpanded = selectedDeployment === deployment.id;
            const progress = getProgressPercentage(deployment);

            return (
              <div
                key={deployment.id}
                className="bg-white rounded-lg shadow overflow-hidden transition-all"
              >
                {/* Header */}
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedDeployment(isExpanded ? null : deployment.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{getStatusIcon(deployment.status)}</span>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {deployment.featureName}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Deployment ID: {deployment.id}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                            deployment.status
                          )}`}
                        >
                          {deployment.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      {deployment.stats && (
                        <div className="mt-4">
                          <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                deployment.status === 'failed'
                                  ? 'bg-red-600'
                                  : deployment.status === 'completed'
                                  ? 'bg-green-600'
                                  : 'bg-blue-600'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <div className="flex gap-6 mt-2 text-sm">
                            <span className="text-green-600">
                              ✓ {deployment.stats.success} succeeded
                            </span>
                            {deployment.stats.failed > 0 && (
                              <span className="text-red-600">
                                ✗ {deployment.stats.failed} failed
                              </span>
                            )}
                            {deployment.stats.pending > 0 && (
                              <span className="text-yellow-600">
                                ⏳ {deployment.stats.pending} pending
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Version:</span>
                          <span className="ml-2 font-medium">
                            {deployment.version || deployment.featureVersion}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Started:</span>
                          <span className="ml-2 font-medium">
                            {formatDate(deployment.createdAt)}
                          </span>
                        </div>
                        {deployment.completedAt && (
                          <div>
                            <span className="text-gray-500">Completed:</span>
                            <span className="ml-2 font-medium">
                              {formatDate(deployment.completedAt)}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">Duration:</span>
                          <span className="ml-2 font-medium">
                            {formatDuration(deployment.createdAt, deployment.completedAt)}
                          </span>
                        </div>
                      </div>

                      {/* Error */}
                      {deployment.error && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                          <div className="flex items-start">
                            <span className="text-red-600 mr-2">⚠️</span>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-red-800">Error</p>
                              <p className="text-sm text-red-700 mt-1">{deployment.error}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-4">
                      {deployment.status === 'in_progress' && onCancel && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancel(deployment.id);
                          }}
                          className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                        >
                          Cancel
                        </button>
                      )}
                      {deployment.status === 'failed' && onRetry && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRetry(deployment.id);
                          }}
                          className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                        >
                          Retry
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDeployment(isExpanded ? null : deployment.id);
                        }}
                        className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                      >
                        {isExpanded ? 'Hide Details' : 'Show Details'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-6">
                    {/* Target Repositories */}
                    {deployment.targetRepos && deployment.targetRepos.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">
                          Target Repositories ({deployment.targetRepos.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {deployment.targetRepos.map((repo) => (
                            <div
                              key={repo}
                              className="px-3 py-2 bg-white rounded-md text-sm text-gray-700 border border-gray-200"
                            >
                              {repo}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Workflow Steps */}
                    {workflow && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">
                          Workflow Steps
                        </h4>
                        <div className="space-y-2">
                          {workflow.steps.map((step, index) => (
                            <div
                              key={step.id}
                              className="flex items-center gap-3 p-3 bg-white rounded-md border border-gray-200"
                            >
                              <div className="flex-shrink-0">
                                {step.status === 'completed' && (
                                  <span className="text-green-600">✅</span>
                                )}
                                {step.status === 'running' && (
                                  <span className="text-blue-600 animate-spin">🔄</span>
                                )}
                                {step.status === 'failed' && (
                                  <span className="text-red-600">❌</span>
                                )}
                                {step.status === 'pending' && (
                                  <span className="text-gray-400">⏳</span>
                                )}
                                {step.status === 'skipped' && (
                                  <span className="text-gray-400">⊘</span>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-900">
                                    {index + 1}. {step.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {step.status.toUpperCase()}
                                  </span>
                                </div>
                                {step.startedAt && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {step.completedAt
                                      ? `Completed in ${formatDuration(
                                          step.startedAt,
                                          step.completedAt
                                        )}`
                                      : `Running for ${formatDuration(step.startedAt)}`}
                                  </div>
                                )}
                                {step.error && (
                                  <div className="text-xs text-red-600 mt-1">
                                    Error: {step.error}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
